<script lang="ts">
	import { _ } from 'svelte-i18n';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import SeoContent from '$lib/components/sections/SeoContent.svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	import Card from '$lib/components/primitives/Card.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';

	const calendarIntegrations = $derived([
		{
			id: 'google',
			name: $_('integrations.calendars.google.name'),
			icon: 'calendar',
			description: $_('integrations.calendars.google.description'),
			features: [
				$_('integrations.calendars.google.feature1'),
				$_('integrations.calendars.google.feature2'),
				$_('integrations.calendars.google.feature3'),
				$_('integrations.calendars.google.feature4')
			],
			color: 'rgb(255, 94, 126)',
			bgColor: 'rgba(255, 94, 126, 0.1)'
		},
		{
			id: 'microsoft',
			name: $_('integrations.calendars.microsoft.name'),
			icon: 'calendar',
			description: $_('integrations.calendars.microsoft.description'),
			features: [
				$_('integrations.calendars.microsoft.feature1'),
				$_('integrations.calendars.microsoft.feature2'),
				$_('integrations.calendars.microsoft.feature3'),
				$_('integrations.calendars.microsoft.feature4')
			],
			color: 'rgb(255, 94, 126)',
			bgColor: 'rgba(255, 94, 126, 0.1)'
		}
	]);

	const financialIntegration = $derived({
		name: $_('integrations.banking.name'),
		description: $_('integrations.banking.description'),
		stats: {
			banks: '2500+',
			countries: '30+',
			transactions: $_('integrations.banking.stats.transactions')
		},
		features: [
			$_('integrations.banking.feature1'),
			$_('integrations.banking.feature2'),
			$_('integrations.banking.feature3'),
			$_('integrations.banking.feature4'),
			$_('integrations.banking.feature5'),
			$_('integrations.banking.feature6')
		],
		supportedRegions: [
			$_('integrations.banking.region1'),
			$_('integrations.banking.region2'),
			$_('integrations.banking.region3'),
			$_('integrations.banking.region4')
		]
	});

	const otherIntegrations = [];
</script>

<svelte:head>
	<title>{$_('integrations.meta.title')}</title>
	<meta name="description" content={$_('integrations.meta.description')} />
	<meta property="og:title" content="{$_('integrations.meta.title')}" />
	<meta property="og:description" content={$_('integrations.meta.description')} />
</svelte:head>

<Header />

<div class="integrations-page">
	<!-- Hero Section -->
	<section class="hero">
		<div class="container">
			<div class="hero__content">
				<div class="hero__badge">
					<Icon name="link" size={16} />
					<span>{$_('integrations.hero.badge')}</span>
				</div>
				<h1 class="hero__title">
					{@html $_('integrations.hero.title')}
				</h1>
				<p class="hero__subtitle">
					{$_('integrations.hero.subtitle')}
				</p>
			</div>
		</div>
	</section>

	<!-- Integration Story Section -->
	<section class="story">
		<div class="container">
			<div class="story__content">
				<h2 class="story__title">{$_('integrations.story.title')}</h2>
				<div class="story__grid">
					<div class="story-item">
						<div class="story-item__icon">
							<Icon name="calendar" size={24} />
						</div>
						<div class="story-item__content">
							<h3>{$_('integrations.story.calendar.title')}</h3>
							<p>{$_('integrations.story.calendar.description')}</p>
						</div>
					</div>
					<div class="story-item">
						<div class="story-item__icon">
							<Icon name="credit-card" size={24} />
						</div>
						<div class="story-item__content">
							<h3>{$_('integrations.story.banking.title')}</h3>
							<p>{$_('integrations.story.banking.description')}</p>
						</div>
					</div>
				</div>
				<div class="story__bottom">
					<p>{@html $_('integrations.story.bottom')}</p>
				</div>
			</div>
		</div>
	</section>

	<!-- Calendar Integrations Section -->
	<section class="calendars">
		<div class="container">
			<div class="section__header">
				<div class="section__icon">
					<Icon name="calendar" size={24} />
				</div>
				<h2 class="section__title">{$_('integrations.calendars.title')}</h2>
				<p class="section__subtitle">
					{$_('integrations.calendars.subtitle')}
				</p>
			</div>

			<div class="calendars__grid">
				{#each calendarIntegrations as integration}
					<Card class="integration-card">
						<div class="integration-card__header">
							<div class="integration-card__icon" style="background: {integration.bgColor}; color: {integration.color};">
								<Icon name={integration.icon} size={32} />
							</div>
							<h3 class="integration-card__name">{integration.name}</h3>
						</div>
						<p class="integration-card__description">
							{integration.description}
						</p>
						<div class="integration-card__features">
							{#each integration.features as feature}
								<div class="feature-item">
									<Icon name="check" size={16} style="color: rgb(34, 197, 94);" />
									<span>{feature}</span>
								</div>
							{/each}
						</div>
						<Button href="https://app.heyweek.com/?signup" variant="outline" size="sm" external>
							{$_('integrations.calendars.connect', { values: { name: integration.name } })}
						</Button>
					</Card>
				{/each}
			</div>
		</div>
	</section>

	<!-- Financial Integration Section -->
	<section class="financial">
		<div class="container">
			<div class="financial__wrapper">
				<div class="section__header section__header--left">
					<div class="section__icon">
						<Icon name="credit-card" size={24} />
					</div>
					<h2 class="section__title">{$_('integrations.banking.title')}</h2>
					<p class="section__subtitle">
						{$_('integrations.banking.subtitle')}
					</p>
				</div>
				
				<div class="financial__stats">
					<div class="stat">
						<div class="stat__number">{financialIntegration.stats.banks}</div>
						<div class="stat__label">{$_('integrations.banking.stats.banks')}</div>
					</div>
					<div class="stat">
						<div class="stat__number">{financialIntegration.stats.countries}</div>
						<div class="stat__label">{$_('integrations.banking.stats.countries')}</div>
					</div>
					<div class="stat">
						<div class="stat__number">{financialIntegration.stats.transactions}</div>
						<div class="stat__label">{$_('integrations.banking.stats.updates')}</div>
					</div>
				</div>
				
				<div class="financial__content">
					<div class="financial__info">
						<h3 class="financial__title">{$_('integrations.banking.insights.title')}</h3>
						<p class="financial__description">
							{$_('integrations.banking.insights.description')}
						</p>
					
						<div class="financial__grid">
							{#each financialIntegration.features as feature}
								<div class="financial-feature">
									<Icon name="check-circle" size={18} />
									<span>{feature}</span>
								</div>
							{/each}
						</div>

						<div class="financial__actions">
							<Button href="https://app.heyweek.com/?signup" variant="primary" size="lg" external>
								<Icon name="link" size={18} />
								{$_('integrations.banking.cta.button')}
							</Button>
							<p class="financial__note">{$_('integrations.banking.cta.note')}</p>
						</div>
					</div>
					
					<div class="financial__visual">
						<div class="bank-logos">
							<div class="bank-logos__title">{$_('integrations.banking.banks.title')}</div>
							<div class="bank-logos__grid">
								<div class="bank-logo">Revolut</div>
								<div class="bank-logo">HSBC</div>
								<div class="bank-logo">Barclays</div>
								<div class="bank-logo">Wise</div>
								<div class="bank-logo">N26</div>
								<div class="bank-logo">{$_('integrations.banking.banks.more')}</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Coming Soon Section -->
	<section class="coming-soon">
		<div class="container">
			<div class="section__header">
				<h2 class="section__title">{$_('integrations.comingSoon.title')}</h2>
				<p class="section__subtitle">
					{$_('integrations.comingSoon.subtitle')}
				</p>
			</div>


			<div class="request-integration">
				<p class="request-integration__text">
					{$_('integrations.comingSoon.request.text')}
				</p>
				<Button href="/contact" variant="outline" size="sm">
					{$_('integrations.comingSoon.request.button')}
				</Button>
			</div>
		</div>
	</section>
</div>

<SeoContent slug="integrations" />

<Footer />

<style>
	.integrations-page {
		min-height: 100vh;
		padding-top: var(--space-8);
	}

	:global(.container) {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 1rem;
		width: 100%;
	}

	/* Hero Section */
	.hero {
		padding: var(--space-16) 0;
		background: linear-gradient(180deg, var(--bg-secondary) 0%, transparent 100%);
		text-align: center;
	}

	.hero__content {
		max-width: 800px;
		margin: 0 auto;
	}

	.hero__badge {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-1) var(--space-3);
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-full);
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		margin-bottom: var(--space-4);
	}

	:global(.dark) .hero__badge {
		background: rgba(255, 94, 126, 0.15);
		color: rgb(255, 140, 160);
	}

	.hero__title {
		font-size: clamp(2.5rem, 5vw, 3.5rem);
		font-weight: var(--font-weight-bold);
		line-height: 1.1;
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.02em;
	}

	.hero__subtitle {
		font-size: var(--font-size-xl);
		color: var(--text-secondary);
		line-height: 1.6;
		max-width: 600px;
		margin: 0 auto var(--space-10);
	}
	
	.hero__title :global(.hero__highlight) {
		background: var(--gradient-brand);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		background-clip: text;
	}
	

	/* Story Section */
	.story {
		padding: var(--space-16) 0;
		background: var(--bg-primary);
		border-bottom: 1px solid var(--border-light);
	}
	
	.story__content {
		max-width: 1000px;
		margin: 0 auto;
	}
	
	.story__title {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		text-align: center;
		margin-bottom: var(--space-10);
	}
	
	.story__grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-8);
		margin-bottom: var(--space-8);
	}
	
	.story-item {
		display: flex;
		gap: var(--space-4);
	}
	
	.story-item__icon {
		flex-shrink: 0;
		width: 48px;
		height: 48px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: linear-gradient(135deg, 
			rgba(255, 94, 126, 0.1) 0%, 
			rgba(151, 71, 255, 0.1) 100%);
		color: var(--accent-primary);
		border-radius: var(--radius-lg);
	}
	
	.story-item__content h3 {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-2);
	}
	
	.story-item__content p {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
	}
	
	.story__bottom {
		text-align: center;
		padding: var(--space-6);
		background: var(--bg-secondary);
		border-radius: var(--radius-lg);
	}
	
	.story__bottom p {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		margin: 0;
	}
	
	.story__bottom :global(strong) {
		color: var(--text-primary);
	}
	
	/* Section Headers */
	.section__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}
	
	.section__header--left {
		text-align: left;
		max-width: 600px;
	}
	
	.section__icon {
		width: 56px;
		height: 56px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: linear-gradient(135deg, 
			rgba(255, 94, 126, 0.1) 0%, 
			rgba(151, 71, 255, 0.1) 100%);
		color: var(--accent-primary);
		border-radius: var(--radius-lg);
		margin: 0 auto var(--space-4);
	}
	
	.section__header--left .section__icon {
		margin: 0 0 var(--space-4) 0;
	}

	.section__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.section__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		max-width: 600px;
		margin: 0 auto;
	}

	/* Calendar Integrations */
	.calendars {
		padding: var(--space-20) 0;
	}

	.calendars__grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
		gap: var(--space-8);
	}

	:global(.integration-card) {
		padding: var(--space-8);
		transition: all 0.2s ease;
		border: 1px solid var(--border-light);
	}

	:global(.integration-card:hover) {
		transform: translateY(-4px);
		border-color: var(--border-brand);
		box-shadow: var(--shadow-elevated);
	}

	.integration-card__header {
		display: flex;
		align-items: center;
		gap: var(--space-4);
		margin-bottom: var(--space-4);
	}

	.integration-card__icon {
		width: 64px;
		height: 64px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: var(--radius-lg);
		transition: all 0.2s ease;
	}
	
	:global(.dark) .integration-card__icon {
		background: rgba(255, 94, 126, 0.15) !important;
		color: rgb(255, 140, 160) !important;
	}

	.integration-card__name {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
	}

	.integration-card__description {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
		margin-bottom: var(--space-6);
	}

	.integration-card__features {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		margin-bottom: var(--space-6);
	}

	.feature-item {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		font-size: var(--font-size-sm);
		color: var(--text-primary);
	}

	/* Financial Section */
	.financial {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
	}

	.financial__wrapper {
		max-width: 1200px;
		margin: 0 auto;
	}
	
	.financial__content {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-12);
		align-items: start;
		margin-top: var(--space-8);
	}
	
	.financial__info {
		padding: var(--space-8);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-xl);
	}


	.financial__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.financial__description {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: 1.6;
		margin-bottom: var(--space-8);
	}

	.financial__stats {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: var(--space-8);
		padding: var(--space-6);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-xl);
		margin-bottom: var(--space-8);
	}

	.stat {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--space-1);
		position: relative;
	}
	
	.stat:not(:last-child)::after {
		content: '';
		position: absolute;
		right: calc(var(--space-8) / -2);
		top: 50%;
		transform: translateY(-50%);
		width: 1px;
		height: 40px;
		background: var(--border-light);
	}

	.stat__number {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-bold);
		color: rgb(255, 94, 126);
	}

	.stat__label {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}

	.financial__grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-3);
		margin-bottom: var(--space-6);
	}
	
	.financial-feature {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		font-size: var(--font-size-sm);
		color: var(--text-primary);
	}
	
	.financial-feature :global(svg) {
		color: rgb(34, 197, 94);
		flex-shrink: 0;
	}
	
	.financial__actions {
		text-align: left;
	}
	
	.financial__actions :global(.button) {
		width: 100%;
		justify-content: center;
		gap: var(--space-2);
		display: inline-flex;
		align-items: center;
	}
	
	.financial__note {
		text-align: center;
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		margin-top: var(--space-3);
	}
	
	.financial__visual {
		display: flex;
		align-items: center;
		justify-content: center;
	}
	
	.bank-logos {
		padding: var(--space-8);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-xl);
		width: 100%;
	}
	
	.bank-logos__title {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		text-align: center;
		margin-bottom: var(--space-4);
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}
	
	.bank-logos__grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: var(--space-3);
	}
	
	.bank-logo {
		padding: var(--space-3);
		background: var(--bg-secondary);
		border-radius: var(--radius-md);
		text-align: center;
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		color: var(--text-primary);
	}
	







	/* Coming Soon Section */
	.coming-soon {
		padding: var(--space-20) 0;
	}







	/* Request Integration */
	.request-integration {
		text-align: center;
		padding: var(--space-8);
		background: var(--bg-secondary);
		border-radius: var(--radius-lg);
	}

	:global(.dark) .request-integration {
		background: var(--bg-tertiary);
	}

	.request-integration__text {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		margin-bottom: var(--space-4);
	}

	/* Responsive */
	@media (max-width: 968px) {
		.calendars__grid {
			grid-template-columns: 1fr;
		}

		.financial__content {
			grid-template-columns: 1fr;
			gap: var(--space-8);
		}

		.financial__visual {
			order: -1;
		}
		
		.story__grid {
			grid-template-columns: 1fr;
			gap: var(--space-6);
		}
	}

	@media (max-width: 768px) {
		.hero__title {
			font-size: 2rem;
		}

		.financial__grid {
			grid-template-columns: 1fr;
		}

		.financial__stats {
			flex-direction: column;
			gap: var(--space-4);
		}
		
		.stat:not(:last-child)::after {
			display: none;
		}

		
		.bank-logos__grid {
			grid-template-columns: repeat(2, 1fr);
		}
	}
</style>